import { type NodeChange } from '@xyflow/react';
import { useEffect, useRef } from 'react';

import { useChatComposerStore } from '@air/feature-ag-ui-chat';

import { useEditCanvasStore } from '../../store';
import { type AppNode } from '../../types';
import { hasCanvasKeyboardOwningOverlay } from '../../utils/hasCanvasKeyboardOwningOverlay';

export type UseEscShortcutParams = {
  isFullscreen?: boolean;
  onCloseCanvas?: () => void;
  onExitFullscreen?: () => void;
  /** Escape closes the canvas even with a node selected, instead of deselecting first. */
  escapeSkipsDeselect?: boolean;
};

export const useEscShortcut = ({
  escapeSkipsDeselect,
  isFullscreen,
  onCloseCanvas,
  onExitFullscreen,
}: UseEscShortcutParams) => {
  const canvasMode = useEditCanvasStore((state) => state.canvasMode);

  // Track whether nodes were selected BEFORE ReactFlow's Escape handler deselects them.
  // ReactFlow handles Escape on the node element (bubbling phase), which fires before
  // document-level listeners. By the time a document keydown handler runs, the store
  // already has no selected nodes. A capturing-phase listener on document fires first.
  const hadSelectedNodesRef = useRef<boolean>(false);
  /** If true at capture, chat composer @, /, or skill-word suggestions, or the null-state prompt's / list, were open — do not close canvas on Escape. */
  const hadChatSuggestionRef = useRef<boolean>(false);

  useEffect(() => {
    useEditCanvasStore.getState().setCloseCanvasOnEscape(escapeSkipsDeselect ? onCloseCanvas : undefined);

    return () => {
      useEditCanvasStore.getState().setCloseCanvasOnEscape(undefined);
    };
  }, [escapeSkipsDeselect, onCloseCanvas]);

  useEffect(() => {
    if (canvasMode.mode !== 'default' || !(onCloseCanvas || onExitFullscreen)) {
      return;
    }

    const handleCapture = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && e.isTrusted) {
        const { nodes, nullStateSkillSuggestionOpen } = useEditCanvasStore.getState();
        hadSelectedNodesRef.current = nodes.some((n) => n.selected);
        const { chatMentionSuggestionOpen, chatSkillWordSuggestionOpen, chatSlashCommandSuggestionOpen } =
          useChatComposerStore.getState();
        hadChatSuggestionRef.current =
          chatMentionSuggestionOpen ||
          chatSlashCommandSuggestionOpen ||
          chatSkillWordSuggestionOpen ||
          nullStateSkillSuggestionOpen;
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !e.isTrusted) {
        return;
      }

      // A modal or floating panel (asset picker) open over the canvas dismisses itself
      // on Escape and owns the keypress, so bail entirely: closing the canvas or even
      // deselecting nodes underneath would be unintended. Queried live since overlay
      // presence is dynamic. Radix menus handle Escape in document capture and unmount
      // before this listener runs, so the live query misses them; defaultPrevented doesn't.
      if (e.defaultPrevented || hasCanvasKeyboardOwningOverlay()) {
        return;
      }

      // biome-ignore lint/suspicious/noUnnecessaryConditions: capture-phase write; Biome treats useRef(false) as always false
      if (hadChatSuggestionRef.current) {
        hadChatSuggestionRef.current = false;
        // Let suggestion plugin + composer handle Escape; do not close canvas or fullscreen
        return;
      }

      // biome-ignore lint/suspicious/noUnnecessaryConditions: capture-phase write; Biome treats useRef(false) as always false
      if (hadSelectedNodesRef.current && !escapeSkipsDeselect) {
        hadSelectedNodesRef.current = false;

        // ReactFlow only deselects nodes when the node element has DOM focus.
        // Nodes selected via the store (not focus) must be deselected manually.
        const { nodes } = useEditCanvasStore.getState();
        const stillSelected = nodes.filter((n) => n.selected);

        if (stillSelected.length > 0) {
          useEditCanvasStore
            .getState()
            .onNodesChange(
              stillSelected.map((n): NodeChange<AppNode> => ({ id: n.id, selected: false, type: 'select' })),
            );
        }

        return;
      }

      e.preventDefault();
      if (isFullscreen && onExitFullscreen) {
        onExitFullscreen();
        return;
      }

      onCloseCanvas?.();
    };

    // window capture runs before document capture (e.g. Radix DismissableLayer), so we snapshot
    // chat suggestion flags before exitSuggestion/onExit clears the store.
    globalThis.window.addEventListener('keydown', handleCapture, true);
    // Bubble phase: decide whether to close/exit
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      globalThis.window.removeEventListener('keydown', handleCapture, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [canvasMode.mode, escapeSkipsDeselect, isFullscreen, onCloseCanvas, onExitFullscreen]);
};
